import { animate, state, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { isNullOrUndefined } from '@sla-ng/core';
import { ConfirmDialogComponent } from '@sla-ng/ui-material';
import * as moment from 'moment';
import { distinctUntilChanged, Observable, Subject, takeUntil } from 'rxjs';
import { map } from 'rxjs/operators';
import { ICostCenterGroup } from '../../../models/cost-center-group.model';
import { ICostCenter } from '../../../models/cost-center.model';
import { ICostCenterGroupDetail } from '../../../models/util/kostenstellengruppe-detail.model';
import { ICostCenterState } from '../../../services/facades/master-data/cost-center-state.model';
import { CostCenterFacade } from '../../../services/facades/master-data/cost-center.facade';
import { AssignedReferenceFiguresDialogComponent } from './assigned-reference-figures-dialog/assigned-reference-figures-dialog.component';
import { CostCenterDialogComponent } from './cost-center-dialog/cost-center-dialog.component';
import { CostCenterGroupDialogComponent } from './cost-center-group-dialog/cost-center-group-dialog.component';
import { HourlyWageDialogComponent } from './hourly-wage-dialog/hourly-wage-dialog.component';

@Component({
    templateUrl: './cost-center.page.html',
    styleUrls: [ './cost-center.page.scss' ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({ height: '0px', minHeight: '0' })),
            state('expanded', style({ height: '*' })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
})
export class CostCenterPage implements OnInit, OnDestroy {
    public dataSource: MatTableDataSource<ICostCenterGroupDetail> = new MatTableDataSource<ICostCenterGroupDetail>([]);
    public isExpandedMap: Record<number, boolean> = {};
    public isExpandAll: boolean = false;

    public costCenterState$: Observable<ICostCenterState>;
    public formGroup = this.myFormBuilder.group({
        start: null as moment.Moment,
        end: null as moment.Moment,
    });
    private destroy$: Subject<void> = new Subject();


    constructor(private myCostCenterFacade: CostCenterFacade,
                private myMatDialog: MatDialog,
                private myFormBuilder: FormBuilder) {
    }

    public ngOnInit(): void {
        this.costCenterState$ = this.myCostCenterFacade.subState();
        this.costCenterState$
            .pipe(
                takeUntil(this.destroy$),
                map((s: ICostCenterState) => s.costCenterGroupDetail),
                distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)))
            .subscribe((data: ICostCenterGroupDetail[]) => {
                this.dataSource.data = data;

                this.dataSource.filterPredicate = ( costCenterGroup: ICostCenterGroupDetail, filter: string ): boolean => {
                    const filterValue = filter.trim().toLowerCase();

                    const firstMatch = costCenterGroup.name.trim().toLowerCase().includes(filterValue)
                        || costCenterGroup.processOrder.toString().trim().toLowerCase().includes(filterValue);

                    const filteredCostCenters = costCenterGroup.costCenters.filter(costCenter =>
                        costCenter.name.trim().toLowerCase().includes(filterValue) ||
                        costCenter.shortName.trim().toLowerCase().includes(filterValue),
                    );
                    // group or element of group = filter value
                    if (firstMatch || filteredCostCenters.length > 0) {
                        // Update costCenterGroup with filtered costCenters
                        costCenterGroup.costCenters = filteredCostCenters;
                        return true;
                    }
                    // no matches
                    return false;
                };
            });

        // Subscribe to daterange filter changes
        this.formGroup.valueChanges.subscribe( v => {
            if ((moment.isMoment(v.start) && moment.isMoment(v.end))
            || (isNullOrUndefined(v.start) && isNullOrUndefined(v.end))) {
                void this.myCostCenterFacade.fetchCostCenters(v.start, v.end);
            }
        });
        void this.myCostCenterFacade.fetchData();
    }

    public ngOnDestroy(): void {
        this.myCostCenterFacade.clearState();
    }

    public onEditKostenstelle(costCenter: ICostCenter): void {
        const dialog: MatDialogRef<CostCenterDialogComponent> = this.myMatDialog.open(CostCenterDialogComponent, {
            width: '600px',
            data: costCenter,
        });
        dialog.afterClosed().subscribe((update: boolean) => {
            if (update) {
                const filteredDaterange = this.formGroup.value;
                void this.myCostCenterFacade.fetchCostCenters(filteredDaterange.start, filteredDaterange.end);
            }
        });
    }

    public onAddKostenstelle(costCenterGroupId: number): void {
        const filteredDaterange = this.formGroup.value;
        const dialog: MatDialogRef<CostCenterDialogComponent> = this.myMatDialog.open(CostCenterDialogComponent, {
            width: '600px',
            data: { costCenterGroupId, validFrom: filteredDaterange.start, validTo: filteredDaterange.end },
        });
        dialog.afterClosed().subscribe((update: boolean) => {
            if (update) {
                void this.myCostCenterFacade.fetchCostCenters(filteredDaterange.start, filteredDaterange.end);
            }
        });
    }

    public applyFilter(filterValue: string): void {
        filterValue = filterValue.trim().toLowerCase(); // Remove whitespace & MatTableDataSource defaults to lowercase matches
        this.dataSource.filter = filterValue;
    }

    public toggleExpand(kostengruppe: ICostCenterGroupDetail): void {
        this.isExpandedMap[ kostengruppe.id ] = !this.isExpandedMap[ kostengruppe.id ];
    }

    public expandAll(): void {
        this.isExpandedMap = {};
        for (const gruppe of this.dataSource.data) {
            this.isExpandedMap[ gruppe.id ] = true;
        }
        this.isExpandAll = true;
    }

    public collapseAll(): void {
        this.isExpandedMap = {};
        this.isExpandAll = false;
    }

    public showBezugsgroessenDialog(selectedKostenstelle: ICostCenter): void {
        this.myCostCenterFacade.setSelectedKostenstelle(selectedKostenstelle);
        this.myMatDialog.open(AssignedReferenceFiguresDialogComponent, {
            height: '70%',
            width: '70%',
            data: selectedKostenstelle,
        });
    }

    public onDeleteCostCenterGroup(CostCenterGroup: ICostCenterGroup): void {
        const dialog: MatDialogRef<ConfirmDialogComponent> = this.myMatDialog.open(ConfirmDialogComponent, {
            width: '600px',
            data: {
                message: 'app.page.cost-center.confirm-delete-group.label',
                messageParams: { name: CostCenterGroup.name },
            },
        });
        dialog.afterClosed().subscribe(async (isConfirmed: boolean) => {
            if (isConfirmed) {
                await this.myCostCenterFacade.deleteCostCenterGroup(CostCenterGroup.id);
            }
        });
    }

    public onDeleteCostCenter(costCenter: ICostCenter): void {
        const dialog: MatDialogRef<ConfirmDialogComponent> = this.myMatDialog.open(ConfirmDialogComponent, {
            width: '600px',
            data: {
                message: 'app.page.cost-center.confirm-delete.label',
                messageParams: { name: costCenter.name },
            },
        });
        dialog.afterClosed().subscribe(async (isConfirmed: boolean) => {
            if (isConfirmed) {
                await this.myCostCenterFacade.deleteCostCenter(costCenter.id);
            }
        });
    }

    public onOpenCostCenterGroupDialog(group?: ICostCenterGroup): void {
        const dialog: MatDialogRef<CostCenterGroupDialogComponent> = this.myMatDialog.open(CostCenterGroupDialogComponent, {
            width: '600px',
            data: isNullOrUndefined(group) ? {} : group,
        });
        dialog.afterClosed().subscribe((update: boolean) => {
            if (update) {
                const filteredDaterange = this.formGroup.value;
                void this.myCostCenterFacade.fetchCostCenters(filteredDaterange.start, filteredDaterange.end);
            }
        });
    }

    public onOpenHourlyWageDialog(costCenter: ICostCenter): void {
        this.myCostCenterFacade.setSelectedKostenstelle(costCenter);
        this.myMatDialog.open(HourlyWageDialogComponent, {
            height: '70%',
            width: '40%',
            data: costCenter,
        });
    }
}
